import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Stack, Group, Text, Badge } from '@repletoryreach/sdk-ui';
import { Alert, WhatsAppPreview } from '@repletoryreach/sdk-ui/data-display';
import { Select, Switch, TokenInput, type TokenSuggestion } from '@repletoryreach/sdk-ui/inputs';
import type { broadcasts, templates as templatesApi } from '@repletoryreach/sdk-api';
import { FileUpload } from '../../../../components/FileUpload';
import type { ParsedComponent } from '../../../../lib/template-components';
import { activeFields, useAttributeSuggestions, useContactFieldDefinitions, type ContactFieldDefinition } from '../../../../lib/attributes';
import {
  CONTACT_FIELDS, acceptForHeader, contactToken, customFieldKey, isContactField, parseVariableValue,
  versionKey, fromVersionKey, type ContactField
} from '../draft';
import { useTemplateLanguageName } from '../languages';
import type { StepProps } from './shared';

/**
 * Step 2: what the message says.
 *
 * A broadcast sends a Meta-approved template, not free text, so this is a
 * choice from a list and a form for its placeholders. Two things the old drawer
 * did not do:
 *
 * - **Personalisation.** A broadcast carried one set of values for the whole
 *   audience, so "Hi {{1}}" could only greet four thousand people by the same
 *   name. A placeholder can now be filled from a contact field instead, with a
 *   fallback for the people who have nothing in it. The picker exists so nobody
 *   has to learn the token syntax, and so a typo cannot reach a customer as
 *   literal braces. The organisation's own fields (`fields.<key>`) are offered
 *   beside the four every contact has, and the literal box takes `{{` as
 *   well, for a value that mixes words and a field ("Order {{contact.fields.order_no}}").
 * - **A preview of a real message.** The bubble is filled with an actual
 *   contact out of this audience, and the values in it were resolved by the
 *   server with the same code the sender uses. It is what that person will
 *   receive, not an illustration.
 */
export function MessageStep({
  state, set, errors, t,
  sendable, template, versions, perLanguage, templatesLoading, blockedByDevFallback, components, vars, mediaHeader,
  preview, previewing
}: StepProps & {
  sendable: templatesApi.MessageTemplate[];
  /** The chosen version of the chosen template, or null. */
  template: templatesApi.MessageTemplate | null;
  /** Every approved version of the chosen template, by Meta's language code. */
  versions: string[];
  /** Whether each person is sent the version in their language (on by default with more than one version). */
  perLanguage: boolean;
  templatesLoading: boolean;
  blockedByDevFallback: number;
  components: ParsedComponent[];
  vars: string[];
  mediaHeader: ParsedComponent | null;
  preview: broadcasts.BroadcastPreview | null;
  previewing: boolean;
}) {
  const body = components.find((c) => c.type === 'BODY')?.text ?? '';
  const footer = components.find((c) => c.type === 'FOOTER')?.text ?? '';
  const header = components.find((c) => c.type === 'HEADER') ?? null;
  const buttons = components.flatMap((c) => c.buttons ?? []);

  const suggestions = useAttributeSuggestions();
  const languageName = useTemplateLanguageName();
  const { fields } = useContactFieldDefinitions();
  const customFields = useMemo(() => activeFields(fields), [fields]);

  /**
   * The recipient the preview is built from, and the values they will actually
   * see. The server resolved them; nothing is substituted here beyond dropping
   * each value into its own placeholder position.
   */
  const sample = preview?.sample?.[0] ?? null;
  const shown = useMemo(() => {
    if (sample?.variables) {
      try {
        const parsed = JSON.parse(sample.variables) as Record<string, string>;
        return { values: parsed, real: true };
      } catch { /* fall through to what has been typed */ }
    }
    return { values: state.variables, real: false };
  }, [sample?.variables, state.variables]);

  function setVar(position: string, value: string) {
    set('variables', { ...state.variables, [position]: value });
  }

  return (
    <Stack gap="lg">
      <Select
        label={t('broadcasts.columns.template')}
        description={t('broadcasts.form.templateHint')}
        error={errors.templateCode}
        // A template is approved per language, one row each: the option names the code and the version, so a
        // second language of the same template can be chosen at all (it used to fold into the first).
        value={template ? versionKey(template.code, template.language) : ''}
        onChange={(e) => {
          const [code, language] = fromVersionKey(e.currentTarget.value);
          // A different template has different placeholders, so old values would
          // land on the wrong positions. Cleared rather than carried across. The
          // language switch goes back to its default for the new template too.
          if (code !== state.templateCode) { set('variables', {}); set('perRecipientLanguage', null); }
          set('templateCode', code);
          set('templateLanguage', language);
        }}
        data={[
          { value: '', label: t('common.select') },
          ...sendable.map((tpl) => ({ value: versionKey(tpl.code, tpl.language), label: `${tpl.code} (${tpl.language})` }))
        ]}
      />

      {/* Send each person the version in their language (1 November 2026). Offered only when the template is
          approved in more than one language, and on by default then. The price is per message, so it changes
          nothing about the cost. */}
      {template && versions.length > 1 && (
        <Switch
          label={t('broadcasts.new.message.perLanguage')}
          description={t('broadcasts.new.message.perLanguageHint', { language: languageName(template.language) })}
          checked={perLanguage}
          onChange={(on) => set('perRecipientLanguage', on)}
        />
      )}

      {/* Said plainly, with the way out, rather than an empty list. */}
      {sendable.length === 0 && !templatesLoading && (
        <Alert color="info" title={t('broadcasts.new.message.noneApproved.title')}>
          <Stack gap={6}>
            <Text role="bodySmall">{t('broadcasts.new.message.noneApproved.body')}</Text>
            <Link to="/templates" className="rr-bc-new__link">{t('broadcasts.new.message.noneApproved.action')}</Link>
          </Stack>
        </Alert>
      )}

      {blockedByDevFallback > 0 && (
        <Alert color="warning">{t('broadcasts.form.devFallbackHidden', { count: blockedByDevFallback })}</Alert>
      )}

      {state.templateCode && (
        <div className="rr-bc-new__compose">
          <Stack gap="md">
            {/* A media header's file is per send. The example attached at
                approval is for Meta's reviewers, so a media template sent
                without one is refused for every recipient. */}
            {mediaHeader && (
              <FileUpload
                label={t('broadcasts.form.headerMedia', {
                  format: t(`templates.header.format.${(mediaHeader.format ?? '').toLowerCase()}`, mediaHeader.format ?? '')
                })}
                description={t('broadcasts.form.headerMedia.hint')}
                error={errors.headerMedia}
                accept={acceptForHeader(mediaHeader.format)}
                value={state.headerMediaUrl}
                onChange={(next) => set('headerMediaUrl', next?.url ?? '')}
              />
            )}

            {vars.length === 0 ? (
              <Text role="bodySmall" c="var(--rr-color-text-muted)">
                {t('broadcasts.new.message.noPlaceholders')}
              </Text>
            ) : (
              vars.map((v) => (
                <VariableField
                  key={v}
                  position={v}
                  value={state.variables[v] ?? ''}
                  error={errors[`var-${v}`]}
                  onChange={(next) => setVar(v, next)}
                  suggestions={suggestions}
                  customFields={customFields}
                  t={t}
                />
              ))
            )}
          </Stack>

          <Stack gap={8} className="rr-bc-new__preview">
            <Group gap="xs">
              <Text role="caption" c="var(--rr-color-text-muted)">{t('broadcasts.form.preview')}</Text>
              {previewing && <Badge size="xs" variant="light">{t('common.loading')}</Badge>}
            </Group>

            <WhatsAppPreview
              chrome
              header={header ? { text: header.text, format: header.format, mediaUrl: state.headerMediaUrl } : null}
              body={fill(body, shown.values)}
              footer={footer ? fill(footer, shown.values) : null}
              buttons={buttons.map((b) => ({ ...b, text: fill(b.text, shown.values) }))}
              emptyBodyLabel={t('broadcasts.new.message.previewEmpty')}
              businessName={t('broadcasts.new.message.previewBusiness')}
              // Any placeholder still unfilled reads as a placeholder rather
              // than as text a customer would be sent.
              renderPlaceholder={(pos) => <em className="rr-bc-new__placeholder">{`{{${pos}}}`}</em>}
            />

            <Text role="caption" c="var(--rr-color-text-muted)">
              {shown.real && sample
                ? t('broadcasts.new.message.previewFor', { who: sample.name || sample.msisdn })
                : t('broadcasts.new.message.previewNoContact')}
            </Text>
          </Stack>
        </div>
      )}
    </Stack>
  );
}

/** A heading row inside the native select: never chosen, only read. */
const FIELDS_HEADING = '__your_fields__';

/**
 * One placeholder: a literal, or a contact field with a fallback.
 *
 * The fallback is not decoration. WhatsApp refuses a template parameter that is
 * empty, so a contact with no first name and no fallback fails that person's
 * send rather than merely reading oddly, which is why the field is offered
 * beside the picker and not hidden behind it.
 */
function VariableField({
  position, value, error, onChange, suggestions, customFields, t
}: {
  position: string;
  value: string;
  error?: string;
  onChange: (next: string) => void;
  suggestions: TokenSuggestion[];
  customFields: ContactFieldDefinition[];
  t: StepProps['t'];
}) {
  const fill = parseVariableValue(value);

  const options = useMemo(() => {
    const out: Array<{ value: string; label: string; disabled?: boolean }> = [
      { value: '', label: t('broadcasts.new.message.fill.literal') },
      ...CONTACT_FIELDS.map((f) => ({ value: f, label: t(`broadcasts.new.message.field.${f}`) }))
    ];
    if (customFields.length > 0) {
      out.push({ value: FIELDS_HEADING, label: t('attributes.group.fields'), disabled: true });
      for (const f of customFields) out.push({ value: `fields.${f.key}`, label: f.label || f.key });
    }
    // A draft may name a field that has since been retired: it still resolves
    // at send time, so it stays selectable rather than silently blank.
    if (fill.field && !out.some((o) => o.value === fill.field)) {
      out.push({ value: fill.field, label: customFieldKey(fill.field) ?? fill.field });
    }
    return out;
  }, [customFields, fill.field, t]);

  return (
    <Stack gap={6} className="rr-bc-new__var">
      <Group gap="xs" align="flex-end" wrap="wrap">
        <Select
          label={t('broadcasts.new.message.fillWith', { index: position })}
          value={fill.field ?? ''}
          onChange={(e) => {
            const next = e.currentTarget.value;
            if (next === '' || next === FIELDS_HEADING) { onChange(''); return; }
            if (isContactField(next)) onChange(contactToken(next as ContactField, fill.fallback));
          }}
          data={options}
        />
        {fill.field ? (
          <TokenInput
            label={t('broadcasts.new.message.fallback')}
            description={t('broadcasts.new.message.fallbackHint')}
            error={error}
            tokens={[]}
            value={fill.fallback}
            onChange={(e) => { const next = e.currentTarget.value; onChange(contactToken(fill.field!, next)); }}
          />
        ) : (
          <TokenInput
            label={t('broadcasts.form.variable', { index: position })}
            description={t('broadcasts.new.message.literalHint')}
            error={error}
            tokens={suggestions}
            fallbackHint={t('attributes.fallbackHint')}
            value={fill.literal}
            // Read before the updater runs: React clears the event's
            // currentTarget once the handler returns.
            onChange={(e) => { const next = e.currentTarget.value; onChange(next); }}
          />
        )}
      </Group>
      {fill.field && (
        <Text role="caption" c="var(--rr-color-text-muted)">
          {t('broadcasts.new.message.personalised')}
        </Text>
      )}
    </Stack>
  );
}

/** Drops each value into its own `{{n}}`, leaving anything unfilled visible. */
function fill(text: string, values: Record<string, string>): string {
  return (text ?? '').replace(/\{\{\s*(\d+)\s*\}\}/g, (whole, index: string) => values[index]?.trim() || whole);
}
